import React, { useState, useEffect } from 'react';
import AppointmentService from '../services/appointmentTimelineService'; // Adjust path as needed

const EditAppointment = ({ appointment, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    appointmentId: '',
    patientName: '',
    patientAvatar: '',
    dateOfBirth: '',
    address: '',
    userid: '',
    meetinguserid: '',
    doctorName: '',
    doctorRole: '',
    date: '',
    startTime: '',
    endTime: '',
    status: 'Pending',
    medicalHistory: '',
    appointmentType: '',
    notes: '',
    checkInTime: '',
    waitTime: 0,
    consultationDuration: 0,
    isaccepted: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Initialize the service
  const appointmentService = new AppointmentService();

  // Initialize form with appointment data
  useEffect(() => {
    if (appointment) {
      setFormData({
        appointmentId: appointment.appointmentId || '',
        patientName: appointment.patientName || '',
        patientAvatar: appointment.patientAvatar || '',
        dateOfBirth: appointment.dateOfBirth || '',
        address: appointment.address || '',
        userid: appointment.userid || '',
        meetinguserid: appointment.meetinguserid || '',
        doctorName: appointment.doctorName || '',
        doctorRole: appointment.doctorRole || '',
        date: appointment.date || '',
        startTime: appointment.startTime || '',
        endTime: appointment.endTime || '',
        status: appointment.status || 'Pending',
        medicalHistory: appointment.medicalHistory || '',
        appointmentType: appointment.appointmentType || '',
        notes: appointment.notes || '',
        checkInTime: appointment.checkInTime ? appointment.checkInTime.split('T')[0] : '',
        waitTime: appointment.waitTime || 0,
        consultationDuration: appointment.consultationDuration || 0,
        isaccepted: appointment.isaccepted || false
      });
    }
  }, [appointment]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.patientName || !formData.date || !formData.startTime) {
        throw new Error('Patient Name, Date, and Start Time are required');
      }

      // Prepare update data (excluding non-editable fields)
      const updateData = {
        patientName: formData.patientName,
        patientAvatar: formData.patientAvatar,
        dateOfBirth: formData.dateOfBirth,
        address: formData.address,
        userid: formData.userid,
        meetinguserid: formData.meetinguserid,
        doctorName: formData.doctorName,
        doctorRole: formData.doctorRole,
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        status: formData.status,
        medicalHistory: formData.medicalHistory,
        appointmentType: formData.appointmentType,
        notes: formData.notes,
        waitTime: parseInt(formData.waitTime) || 0,
        consultationDuration: parseInt(formData.consultationDuration) || 0,
        isaccepted: formData.isaccepted
      };

      // Add checkInTime if provided
      if (formData.checkInTime) {
        updateData.checkInTime = new Date(formData.checkInTime).toISOString();
      }

      // Use the service to update the appointment
      const response = await appointmentService.updateAppointment(appointment.id, updateData);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update appointment' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const updatedAppointment = await response.json();
      
      if (onSave) {
        onSave(updatedAppointment);
      }

      // Close modal and refresh page
      if (onCancel) {
        onCancel();
      }
      
      setTimeout(() => {
        window.location.reload();
      }, 100);

    } catch (error) {
      console.error('Error updating appointment:', error);
      setError(error.message || 'Error updating appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleAcceptAppointment = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await appointmentService.acceptAppointment(appointment.id);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to accept appointment' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      setFormData(prev => ({
        ...prev,
        isaccepted: true,
        status: 'Confirmed'
      }));
      
      alert('Appointment accepted successfully!');
      
    } catch (error) {
      console.error('Error accepting appointment:', error);
      setError(error.message || 'Error accepting appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckinPatient = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await appointmentService.checkinPatient(appointment.id);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to check in patient' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      setFormData(prev => ({
        ...prev,
        checkInTime: new Date().toISOString().split('T')[0],
        status: 'Checked In'
      }));
      
      alert('Patient checked in successfully!');
      
    } catch (error) {
      console.error('Error checking in patient:', error);
      setError(error.message || 'Error checking in patient');
    } finally {
      setLoading(false);
    }
  };

  if (!appointment) {
    return <div className="alert alert-info">No appointment selected for editing</div>;
  }

  return (
    <div className="container-fluid p-0">
      <div className="row align-items-center mb-3">
        <div className="col">
          <h3>Edit Appointment</h3>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Appointment ID</label>
            <input 
              type="text" 
              className="form-control" 
              value={appointment.id || 'N/A'} 
              readOnly 
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Internal Appointment ID</label>
            <input 
              type="text" 
              name="appointmentId"
              className="form-control" 
              value={formData.appointmentId}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Status <span className="text-danger">*</span></label>
            <select 
              name="status"
              className="form-select" 
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="Pending">Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Checked In">Checked In</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="No Show">No Show</option>
            </select>
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Appointment Type</label>
            <select 
              name="appointmentType"
              className="form-select" 
              value={formData.appointmentType}
              onChange={handleChange}
            >
              <option value="">Select Type</option>
              <option value="Consultation">Consultation</option>
              <option value="Follow-up">Follow-up</option>
              <option value="Check-up">Check-up</option>
              <option value="Emergency">Emergency</option>
              <option value="Telemedicine">Telemedicine</option>
              <option value="Vaccination">Vaccination</option>
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="form-check mt-4">
              <input 
                className="form-check-input" 
                type="checkbox" 
                name="isaccepted"
                checked={formData.isaccepted}
                onChange={handleChange}
                id="isAcceptedCheck"
              />
              <label className="form-check-label fw-bold" htmlFor="isAcceptedCheck">
                Accepted
              </label>
            </div>
          </div>
        </div>

        {/* Patient Information */}
        <div className="mb-3">
          <h4>Patient Information</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Patient Name <span className="text-danger">*</span></label>
              <input 
                type="text" 
                name="patientName"
                className="form-control" 
                value={formData.patientName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Patient Avatar URL</label>
              <input 
                type="url" 
                name="patientAvatar"
                className="form-control" 
                value={formData.patientAvatar}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Date of Birth</label>
              <input 
                type="date" 
                name="dateOfBirth"
                className="form-control" 
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">User ID</label>
              <input 
                type="text" 
                name="userid"
                className="form-control" 
                value={formData.userid}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Meeting User ID</label>
              <input 
                type="text" 
                name="meetinguserid"
                className="form-control" 
                value={formData.meetinguserid}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label fw-bold">Address</label>
              <textarea
                name="address"
                className="form-control"
                rows="2"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter patient address"
              ></textarea>
            </div>
          </div>
        </div>

        {/* Doctor Information */}
        <div className="mb-3">
          <h4>Doctor Information</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Doctor Name</label>
              <input 
                type="text" 
                name="doctorName"
                className="form-control" 
                value={formData.doctorName}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Doctor Role</label>
              <input 
                type="text" 
                name="doctorRole"
                className="form-control" 
                value={formData.doctorRole}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Schedule Information */}
        <div className="mb-3">
          <h4>Schedule Information</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Date <span className="text-danger">*</span></label>
              <input 
                type="date" 
                name="date"
                className="form-control" 
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Start Time <span className="text-danger">*</span></label>
              <input 
                type="time" 
                name="startTime"
                className="form-control" 
                value={formData.startTime}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">End Time</label>
              <input 
                type="time" 
                name="endTime"
                className="form-control" 
                value={formData.endTime}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Check-in Date</label>
              <input 
                type="date" 
                name="checkInTime"
                className="form-control" 
                value={formData.checkInTime}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Timing Details */}
        <div className="mb-3">
          <h4>Timing Details</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Wait Time (minutes)</label>
              <input 
                type="number" 
                name="waitTime"
                className="form-control" 
                value={formData.waitTime}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Consultation Duration (minutes)</label>
              <input 
                type="number" 
                name="consultationDuration"
                className="form-control" 
                value={formData.consultationDuration}
                onChange={handleChange}
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Medical Information */}
        <div className="mb-3">
          <h4>Medical Information</h4>
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label fw-bold">Medical History</label>
              <textarea
                name="medicalHistory"
                className="form-control"
                rows="3"
                value={formData.medicalHistory}
                onChange={handleChange}
                placeholder="Enter medical history"
              ></textarea>
            </div>
          </div>
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label fw-bold">Notes</label>
              <textarea
                name="notes"
                className="form-control"
                rows="3"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Enter any additional notes"
              ></textarea>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <button 
              type="submit" 
              className="btn btn-primary me-2"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Appointment'}
            </button>
            
            {!formData.isaccepted && (
              <button 
                type="button" 
                className="btn btn-success me-2"
                onClick={handleAcceptAppointment}
                disabled={loading}
              >
                Accept Appointment
              </button>
            )}
            
            {formData.status !== 'Checked In' && formData.status !== 'Completed' && (
              <button 
                type="button" 
                className="btn btn-info me-2"
                onClick={handleCheckinPatient}
                disabled={loading}
              >
                Check In Patient
              </button>
            )}
            
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditAppointment;